// frontend/src/pages/freelancer/FreelancerDashboard.js
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import FreelancerSidebarLayout from "./FreelancerSidebarLayout";
import OnboardingChecklist from "../../components/OnboardingChecklist";
import GoLivePanel from "../../components/GoLivePanel";
import TrialCountdown from "../../components/TrialCountdown";
import ShareProfileButton from "../../components/ShareProfileButton";
import {
  getFreelancerCompletedSteps,
  shouldShowOnboarding,
} from "../../utils/onboardingHelpers";
import {
  formatDisplayDate,
  getTodayString,
  parseToYYYYMMDD,
} from "../../utils/dateHelpers";
import axios from "axios";
import WalkInButton from "../../components/WalkInButton";

function isFeatureEnabled(flagName) {
  const raw =
    process.env[`REACT_APP_${flagName}`] ?? process.env[flagName] ?? "false";
  return String(raw).toLowerCase() === "true";
}

const ENABLE_NOW_LIVE_MODAL = isFeatureEnabled(
  "OGE_FIX_FREELANCER_NOW_LIVE_MODAL",
);

const ACTIVE_BOOKING_STATUSES = ["confirmed", "pending"];

function FreelancerDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    todayBookings: 0,
    upcomingBookings: 0,
    thisMonthEarnings: 0,
    activeServices: 0,
  });
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [stripeConnected, setStripeConnected] = useState(null);
  const [showStripeNotification, setShowStripeNotification] = useState(false);
  const [staffId, setStaffId] = useState(null);
  const [staffProfile, setStaffProfile] = useState(null); // For trial countdown
  const [subscriptionInfo, setSubscriptionInfo] = useState(null); // Stable trial/subscription state
  const [statusModal, setStatusModal] = useState({
    isOpen: false,
    title: "",
    message: "",
  });

  // Phase E1: Onboarding state
  const [onboardingData, setOnboardingData] = useState(null);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const basePath = "/dashboard/freelancer";
  const openStatusModal = useCallback((title, message) => {
    setStatusModal({ isOpen: true, title, message });
  }, []);
  const getBookingDateString = useCallback(
    (booking) =>
      parseToYYYYMMDD(booking?.appointmentDate || booking?.bookingDate),
    [],
  );

  const getBookingStartDateTime = useCallback(
    (booking) => {
      if (booking?.appointmentDateTimeUTC) {
        const utcDate = new Date(booking.appointmentDateTimeUTC);
        if (!Number.isNaN(utcDate.getTime())) {
          return utcDate;
        }
      }

      const dateString = getBookingDateString(booking);
      const startTime = booking?.startTime || booking?.bookingTime;
      if (!dateString || !startTime) return null;

      const startDateTime = new Date(`${dateString}T${startTime}`);
      return Number.isNaN(startDateTime.getTime()) ? null : startDateTime;
    },
    [getBookingDateString],
  );

  const getBookingSortTime = useCallback(
    (booking) => {
      const startDateTime = getBookingStartDateTime(booking);
      if (startDateTime) {
        return startDateTime.getTime();
      }

      const createdAt = new Date(booking?.createdAt || 0);
      if (!Number.isNaN(createdAt.getTime())) {
        return createdAt.getTime();
      }

      return 0;
    },
    [getBookingStartDateTime],
  );

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  const getApiBaseUrl = () =>
    process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

  // Phase E1: Fetch onboarding status
  const fetchOnboardingStatus = useCallback(async () => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) return;

    try {
      const response = await axios.get(
        `${getApiBaseUrl()}/api/users/onboarding/status`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setOnboardingData(response.data);
    } catch (error) {}
  }, []);

  // Phase E1: Handle onboarding step click
  const handleStepClick = useCallback(
    async (step) => {
      navigate(step.action);
    },
    [navigate],
  );

  // Phase E1: Handle onboarding dismiss
  const handleDismissOnboarding = useCallback(async () => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) return;

    try {
      await axios.post(
        `${getApiBaseUrl()}/api/users/onboarding/dismiss`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setShowOnboarding(false);
    } catch (error) {}
  }, []);

  const fetchDashboardData = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const baseURL = getApiBaseUrl();

      // First check if freelancer profile exists
      try {
        const staffRes = await axios.get(`${baseURL}/api/freelancer/profile`, {
          headers,
        });
        const staff = staffRes.data?.staff || null;
        const resolvedStaffId = staff?.id || staff?._id || null;

        // R4 should see go-live panel; R7 flow is intentionally excluded for now.
        setStaffId(staff?.role === "R4" ? resolvedStaffId : null);
        setStaffProfile(staff); // Store full profile for trial countdown
      } catch (profileError) {
        if (
          profileError.response?.status === 404 ||
          profileError.response?.data?.error === "staff_not_found"
        ) {
          // No staff record - redirect to complete registration
          navigate("/freelancer/profile", { state: { isRetry: true } });
          return;
        }
        throw profileError;
      }

      // Fetch basic stats and Stripe status
      const [
        servicesRes,
        bookingsRes,
        earningsRes,
        stripeRes,
        scheduleRes,
        subscriptionRes,
      ] = await Promise.all([
        axios
          .get(`${baseURL}/api/staff/services`, { headers })
          .catch(() => ({ data: { services: [] } })),
        axios
          .get(`${baseURL}/api/freelancer/bookings`, { headers })
          .catch(() => ({ data: { bookings: [] } })),
        axios
          .get(`${baseURL}/api/staff/earnings`, { headers })
          .catch(() => ({ data: { thisMonth: 0 } })),
        axios
          .get(`${baseURL}/api/freelancer/stripe/status`, { headers })
          .catch(() => ({ data: { connected: false } })),
        axios
          .get(`${baseURL}/api/freelancer/schedule`, { headers })
          .catch(() => ({ data: { schedule: null } })),
        axios
          .get(`${baseURL}/api/subscription-new/status`, { headers })
          .catch(() => ({ data: null })),
      ]);

      const services = servicesRes.data.services || [];
      const bookings = bookingsRes.data.bookings || [];
      const schedule = scheduleRes.data.schedule || null;
      const today = getTodayString();
      const now = new Date();
      const sortedRecentBookings = [...bookings]
        .sort((a, b) => getBookingSortTime(b) - getBookingSortTime(a))
        .slice(0, 5);

      setStats({
        todayBookings: bookings.filter(
          (b) => getBookingDateString(b) === today,
        ).length,
        upcomingBookings: bookings.filter((b) => {
          if (!ACTIVE_BOOKING_STATUSES.includes(b.status?.toLowerCase())) {
            return false;
          }

          const startDateTime = getBookingStartDateTime(b);
          return startDateTime && startDateTime > now;
        }).length,
        thisMonthEarnings: Number(earningsRes.data.thisMonth || 0),
        activeServices: services.filter((s) => s.isActive).length,
      });

      setRecentBookings(sortedRecentBookings);
      setSubscriptionInfo(subscriptionRes.data || null);

      // Check Stripe connection and show notification if not connected
      const isConnected = stripeRes.data.connected || false;
      setStripeConnected(isConnected);

      // Update onboarding with actual schedule
      const hasPhoto = true; // TODO: Get from staff profile
      const hasServices = services.length > 0;
      const hasSchedule = schedule !== null;

      const detected = getFreelancerCompletedSteps(
        hasPhoto,
        hasServices ? [{ id: "1" }] : [],
        hasSchedule ? schedule : null,
        isConnected,
      );

      const mergedSteps = [
        ...new Set([...(onboardingData?.completedSteps || []), ...detected]),
      ];
      setCompletedSteps(mergedSteps);

      const allSteps = ["photo", "services", "schedule", "payment"];
      setShowOnboarding(
        shouldShowOnboarding(onboardingData, mergedSteps, allSteps),
      );

      // Check if user has dismissed the notification (stored in localStorage)
      const dismissed = localStorage.getItem("stripe_notification_dismissed");
      setShowStripeNotification(!isConnected && !dismissed);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [
    getAuthHeaders,
    getBookingDateString,
    getBookingSortTime,
    getBookingStartDateTime,
  ]);

  useEffect(() => {
    fetchDashboardData();
    fetchOnboardingStatus();
  }, [fetchDashboardData, fetchOnboardingStatus]);

  // Handle subscription payment success/cancel redirect from Stripe
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const subscriptionSuccess = urlParams.get("success");
    const subscriptionCanceled = urlParams.get("canceled");

    if (subscriptionSuccess === "true") {
      if (ENABLE_NOW_LIVE_MODAL) {
        openStatusModal(
          "Subscription Activated",
          "Your subscription is active and your dashboard is ready.",
        );
      } else {
        alert("🎉 Subscription activated successfully! Welcome aboard!");
      }
      window.history.replaceState({}, document.title, "/dashboard/freelancer");
      // Refresh page to update trial banner
      setTimeout(() => window.location.reload(), 1000);
    } else if (subscriptionCanceled === "true") {
      if (ENABLE_NOW_LIVE_MODAL) {
        openStatusModal(
          "Checkout Canceled",
          "No worries. You can subscribe anytime to continue using the platform.",
        );
      } else {
        alert(
          "⚠️ Checkout canceled. You can subscribe anytime to continue using the platform.",
        );
      }
      window.history.replaceState({}, document.title, "/dashboard/freelancer");
    }
  }, [openStatusModal]);

  if (loading) {
    return (
      <FreelancerSidebarLayout>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <p>Loading dashboard...</p>
        </div>
      </FreelancerSidebarLayout>
    );
  }

  return (
    <FreelancerSidebarLayout>
      <div style={{ padding: "20px", maxWidth: "1200px", margin: "0 auto" }}>
        <div style={{ marginBottom: "24px" }}>
          <h1
            style={{ fontSize: "28px", fontWeight: "700", marginBottom: "8px" }}
          >
            Freelancer Dashboard
          </h1>
          <span className="freelancer-badge">Independent Professional</span>

          {/* Walk-in (Phase 1): freelancer defaults to themselves. */}
          <div style={{ marginTop: "16px" }}>
            <WalkInButton />
          </div>
          {staffProfile?.slug &&
            (() => {
              const origin = window.location.origin;
              const staffSlug = staffProfile.slug;
              const shareUrl = `${origin}/freelancer/${staffSlug}`;
              const name =
                `${staffProfile.firstName || ""} ${staffProfile.lastName || ""}`.trim();
              return (
                <div style={{ marginTop: "16px" }}>
                  <ShareProfileButton
                    url={shareUrl}
                    title={
                      name ? `${name} on Ogestyle` : "My profile on Ogestyle"
                    }
                  />
                </div>
              );
            })()}
        </div>

        {/* Go-Live Panel - Show when staff profile exists */}
        {staffId && (
          <GoLivePanel
            type="freelancer"
            id={staffId}
            onGoLive={async () => {
              // Refresh data after go-live
              await fetchDashboardData();
              await fetchOnboardingStatus();
              if (ENABLE_NOW_LIVE_MODAL) {
                openStatusModal(
                  "You're Now Live",
                  "Your trial period has started and customers can now discover your profile.",
                );
              } else {
                alert("🎉 You're now live! Trial period has started.");
              }
            }}
          />
        )}

        {statusModal.isOpen && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(15, 23, 42, 0.55)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 3000,
              padding: "20px",
            }}
            onClick={() =>
              setStatusModal({ isOpen: false, title: "", message: "" })
            }
          >
            <div
              style={{
                width: "100%",
                maxWidth: "460px",
                background: "#ffffff",
                borderRadius: "14px",
                boxShadow: "0 18px 40px rgba(0,0,0,0.2)",
                padding: "24px",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ fontSize: "24px", marginBottom: "8px" }}>✅</div>
              <h3
                style={{ margin: "0 0 8px", fontSize: "20px", fontWeight: 700 }}
              >
                {statusModal.title}
              </h3>
              <p style={{ margin: 0, color: "#334155", lineHeight: 1.5 }}>
                {statusModal.message}
              </p>
              <div
                style={{
                  marginTop: "18px",
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <button
                  onClick={() =>
                    setStatusModal({ isOpen: false, title: "", message: "" })
                  }
                  style={{
                    border: "none",
                    borderRadius: "8px",
                    padding: "10px 16px",
                    background: "#0f766e",
                    color: "#fff",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Trial Countdown / Expiry Card */}
        {((subscriptionInfo?.trialEndDate &&
          subscriptionInfo?.subscriptionStatus !== "active") ||
          (staffProfile?.trialEndDate &&
            staffProfile?.subscriptionStatus !== "active")) && (
          <div style={{ marginBottom: "20px" }}>
            <TrialCountdown
              trialEndDate={
                subscriptionInfo?.trialEndDate || staffProfile?.trialEndDate
              }
              userType="freelancer"
              hasPaymentMethod={
                !!subscriptionInfo?.hasPaymentMethod ||
                !!staffProfile?.stripePaymentMethodId
              }
              onExpired={() => {}}
            />
          </div>
        )}

        {/* Stripe Setup Notification */}
        {showStripeNotification && (
          <div
            style={{
              background: "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)",
              padding: "20px",
              borderRadius: "12px",
              marginBottom: "24px",
              boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              color: "#fff",
            }}
          >
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  marginBottom: "8px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <span style={{ fontSize: "24px" }}>⚠️</span>
                Complete Your Stripe Setup
              </div>
              <p
                style={{
                  margin: "0 0 12px 0",
                  fontSize: "14px",
                  opacity: 0.95,
                }}
              >
                You need to connect your Stripe account to start receiving
                payments from clients. This only takes a few minutes!
              </p>
              <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                <button
                  onClick={() =>
                    navigate("/dashboard/freelancer/settings?tab=payments")
                  }
                  style={{
                    padding: "10px 20px",
                    background: "#fff",
                    color: "#f59e0b",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontWeight: "600",
                    fontSize: "14px",
                  }}
                >
                  Set Up Stripe Now
                </button>
                <button
                  onClick={() => {
                    localStorage.setItem(
                      "stripe_notification_dismissed",
                      "true",
                    );
                    setShowStripeNotification(false);
                  }}
                  style={{
                    padding: "10px 20px",
                    background: "transparent",
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.5)",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontWeight: "500",
                    fontSize: "14px",
                  }}
                >
                  Remind Me Later
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: "16px",
            marginBottom: "24px",
          }}
        >
          <div
            style={{
              background: "white",
              padding: "20px",
              borderRadius: "12px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          >
            <div
              style={{
                fontSize: "14px",
                color: "#6b7280",
                marginBottom: "4px",
              }}
            >
              Today's Bookings
            </div>
            <div
              style={{ fontSize: "32px", fontWeight: "700", color: "#3b82f6" }}
            >
              {stats.todayBookings}
            </div>
          </div>

          <div
            style={{
              background: "white",
              padding: "20px",
              borderRadius: "12px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          >
            <div
              style={{
                fontSize: "14px",
                color: "#6b7280",
                marginBottom: "4px",
              }}
            >
              Upcoming
            </div>
            <div
              style={{ fontSize: "32px", fontWeight: "700", color: "#10b981" }}
            >
              {stats.upcomingBookings}
            </div>
          </div>

          <div
            style={{
              background: "white",
              padding: "20px",
              borderRadius: "12px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          >
            <div
              style={{
                fontSize: "14px",
                color: "#6b7280",
                marginBottom: "4px",
              }}
            >
              This Month's Earnings
            </div>
            <div
              style={{ fontSize: "32px", fontWeight: "700", color: "#8b5cf6" }}
            >
              ${stats.thisMonthEarnings.toFixed(2)}
            </div>
            <div
              style={{ fontSize: "12px", color: "#6b7280", marginTop: "6px" }}
            >
              Completed booking earnings for this month
            </div>
          </div>

          <div
            style={{
              background: "white",
              padding: "20px",
              borderRadius: "12px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          >
            <div
              style={{
                fontSize: "14px",
                color: "#6b7280",
                marginBottom: "4px",
              }}
            >
              Active Services
            </div>
            <div
              style={{ fontSize: "32px", fontWeight: "700", color: "#f59e0b" }}
            >
              {stats.activeServices}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div
          style={{
            background: "white",
            padding: "20px",
            borderRadius: "12px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            marginBottom: "24px",
          }}
        >
          <h2
            style={{
              fontSize: "18px",
              fontWeight: "600",
              marginBottom: "16px",
            }}
          >
            Quick Actions
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "12px",
            }}
          >
            <button
              onClick={() => navigate(`${basePath}/services/add`)}
              style={{
                padding: "12px",
                background: "#3b82f6",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: "500",
              }}
            >
              Add Service
            </button>
            <button
              onClick={() => navigate(`${basePath}/products/add`)}
              style={{
                padding: "12px",
                background: "#8b5cf6",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: "500",
              }}
            >
              Add Product
            </button>
            <button
              onClick={() => navigate(`${basePath}/schedule`)}
              style={{
                padding: "12px",
                background: "#10b981",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: "500",
              }}
            >
              Manage Schedule
            </button>
            <button
              onClick={() => navigate(`${basePath}/bookings`)}
              style={{
                padding: "12px",
                background: "#f59e0b",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: "500",
              }}
            >
              View Bookings
            </button>
          </div>
        </div>

        {/* Recent Bookings */}
        <div
          style={{
            background: "white",
            padding: "20px",
            borderRadius: "12px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          <h2
            style={{
              fontSize: "18px",
              fontWeight: "600",
              marginBottom: "16px",
            }}
          >
            Recent Bookings
          </h2>
          {recentBookings.length === 0 ? (
            <p
              style={{ color: "#6b7280", textAlign: "center", padding: "20px" }}
            >
              No bookings yet. Add your services to start receiving bookings!
            </p>
          ) : (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              {recentBookings.map((booking) => (
                <div
                  key={booking._id}
                  style={{
                    padding: "12px",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    cursor: "pointer",
                  }}
                  onClick={() =>
                    navigate(`${basePath}/bookings/${booking._id}`)
                  }
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: "600" }}>
                        {booking.serviceName || "Unknown service"}
                      </div>
                      <div style={{ fontSize: "14px", color: "#6b7280" }}>
                        {formatDisplayDate(
                          booking.appointmentDate || booking.bookingDate,
                        )}
                      </div>
                    </div>
                    <span
                      style={{
                        padding: "4px 12px",
                        borderRadius: "12px",
                        fontSize: "12px",
                        fontWeight: "500",
                        background:
                          booking.status?.toLowerCase() === "confirmed"
                            ? "#d1fae5"
                            : booking.status?.toLowerCase() === "completed"
                              ? "#dbeafe"
                              : booking.status?.toLowerCase() === "cancelled"
                                ? "#fee2e2"
                                : "#fef3c7",
                        color:
                          booking.status?.toLowerCase() === "confirmed"
                            ? "#065f46"
                            : booking.status?.toLowerCase() === "completed"
                              ? "#1d4ed8"
                              : booking.status?.toLowerCase() === "cancelled"
                                ? "#991b1b"
                                : "#92400e",
                      }}
                    >
                      {booking.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </FreelancerSidebarLayout>
  );
}

export default FreelancerDashboard;
